import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { PageHeader, PriorityPill } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Pause, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLS = [
  { key: "pending", label: "Pending", color: "amber" },
  { key: "assigned", label: "Assigned", color: "sky" },
  { key: "in_progress", label: "In Progress", color: "indigo" },
  { key: "paused", label: "Paused", color: "slate" },
  { key: "completed", label: "Completed", color: "emerald" },
  { key: "escalated", label: "Escalated", color: "red" },
];

export default function QueueBoard() {
  const [board, setBoard] = useState({});
  const nav = useNavigate();
  const { user } = useAuth();
  const isMgr = user?.role === "manager";

  const load = async () => {
    const { data } = await api.get("/queue");
    setBoard(data);
  };
  useEffect(() => { load(); }, []);

  const pause = async () => { await api.post("/queue/pause"); toast.success("Queue paused"); load(); };
  const resume = async () => { await api.post("/queue/resume"); toast.success("Queue resumed"); load(); };

  return (
    <div>
      <PageHeader title="Queue Board" subtitle="Kanban across all case states">
        {isMgr && (
          <>
            <Button data-testid="queue-pause-btn" variant="outline" className="h-9" onClick={pause}><Pause size={14} className="mr-1" /> Pause queue</Button>
            <Button data-testid="queue-resume-btn" className="h-9" onClick={resume}><Play size={14} className="mr-1" /> Resume</Button>
          </>
        )}
      </PageHeader>

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {COLS.map(col => {
            const items = board[col.key] || [];
            return (
              <div key={col.key} className="w-80 flex flex-col kanban-column" data-testid={`kanban-col-${col.key}`}>
                <div className="flex items-center justify-between px-3 py-2 mb-3 border-b-2 border-foreground/10">
                  <div className="eyebrow">{col.label}</div>
                  <div className="mono text-xs bg-secondary px-2 py-0.5 rounded">{items.length}</div>
                </div>
                <div className="space-y-2 flex-1">
                  {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">Empty</div>}
                  {items.map(c => (
                    <div
                      key={c.id}
                      data-testid={`kanban-card-${c.case_id}`}
                      onClick={() => nav(`/cases/${c.id}`)}
                      className="border bg-card rounded-md p-3 cursor-pointer hover:border-primary/70 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="mono text-[10px] font-medium">{c.case_id}</span>
                        <PriorityPill priority={c.priority} />
                      </div>
                      <div className="text-sm font-medium truncate">{c.customer}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{c.case_type}</div>
                      {c.assignee_name && <div className="text-[10px] text-muted-foreground mt-1">→ {c.assignee_name}</div>}
                      <div className="text-[10px] mono text-muted-foreground mt-1">Due {formatDate(c.due_date)}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
